import { useEffect, useState } from "react";
import { useLantern, useSession } from "../hooks";
import type { APIToken, Principal, UUID } from "../sync/types";
import { Badge, Card, EmptyState, Field, Page } from "./ui";
import { MadeToken, TokenForm, TokenRows, agentLifetimes, confirmRevoke, personalLifetimes, useAgents } from "./AgentTokens";
import { t, tc } from "../i18n";

type Listing = { tokens: APIToken[]; scopes: string[]; agents: UUID[]; personal_tokens: boolean };

// useTokens is the API tokens one manages, or, for an admin with all, every
// one, and a way to load them again after a change.
function useTokens(all: boolean): [Listing | null, string, () => Promise<void>] {
  const session = useSession();
  const [list, setList] = useState<Listing | null>(null);
  const [error, setError] = useState("");
  const load = () =>
    session.api.apiTokens(all).then(
      (r) => (setList(r), setError("")),
      (err) => setError(err instanceof Error ? err.message : String(err)),
    );
  useEffect(() => {
    void load();
  }, [session, all]);
  return [list, error, load];
}

// AgentPicker chooses which agent a new token is for.
function AgentPicker({ agents, value, onChange }: { agents: Principal[]; value: UUID; onChange: (id: UUID) => void }) {
  return (
    <Field label={tc("token", "For")}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {agents.map((a) => (
          <option key={a.id} value={a.id}>
            {a.display_name || a.handle} (@{a.handle})
          </option>
        ))}
      </select>
    </Field>
  );
}

// APITokensPage is one's API tokens in Settings: personal ones, which one's
// scripts sign in as one with, and those of the agents one runs.
export function APITokensPage() {
  const session = useSession();
  const me = useLantern().me;
  const [list, loadError, reload] = useTokens(false);
  const [agents] = useAgents(false);
  const runnable = agents.filter((a) => list?.agents.includes(a.id));
  const [agent, setAgent] = useState<UUID>("");
  const [shown, setShown] = useState<{ token: string; personal: boolean } | null>(null);
  const [error, setError] = useState("");
  const forAgent = runnable.some((a) => a.id === agent) ? agent : (runnable[0]?.id ?? "");
  const mine = list?.tokens.filter((tk) => tk.principal_id === me?.id) ?? [];
  const agentTokens = list?.tokens.filter((tk) => tk.principal_id !== me?.id) ?? [];

  async function revoke(tk: APIToken) {
    if (!confirmRevoke(tk)) return;
    setError("");
    try {
      await session.api.revokeAPIToken(tk.id);
      setShown(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <Page className="settings" narrow title={t("API tokens")} description={t("What your scripts and your agents sign in to Lantern's API with. A token is shown once, when it is made.")}>
      {(loadError || error) && (
        <p className="error" role="alert">
          {loadError || error}
        </p>
      )}
      <Card
        title={t("Personal tokens")}
        description={t(
          "Your scripts sign in as you with these, to manage pools and tasks or to read and post. They never change how you sign in, make tokens or agents, delegate, or reach Administration.",
        )}
      >
        {shown?.personal && <MadeToken key={shown.token} token={shown.token} onDone={() => setShown(null)} />}
        {!list ? (
          <p className="muted">{t("Loading…")}</p>
        ) : mine.length === 0 ? (
          <EmptyState icon="🔑" title={t("No personal tokens")} />
        ) : (
          <TokenRows tokens={mine} onRevoke={(tk) => void revoke(tk as APIToken)} who={(tk) => !(tk as APIToken).personal && <Badge tone="warn">{t("made with lantern admin; not held back")}</Badge>} />
        )}
        {list &&
          (me?.role === "guest" ? (
            <p className="muted">{t("Guests cannot make API tokens.")}</p>
          ) : !list.personal_tokens ? (
            <p className="notice">{t("An admin turned personal API tokens off in this workspace.")}</p>
          ) : (
            <TokenForm
              title={t("New personal token")}
              scopes={list.scopes}
              lifetimes={personalLifetimes()}
              onMake={async (name, scopes, days) => {
                const r = await session.api.issuePersonalToken(name, scopes, days);
                setShown({ token: r.token, personal: true });
                await reload();
              }}
            />
          ))}
      </Card>
      <Card title={t("Your agents' tokens")} description={t("What the agents you run sign in with. Each agent's page in Agents has its own too.")}>
        {shown && !shown.personal && <MadeToken key={shown.token} token={shown.token} onDone={() => setShown(null)} />}
        {!list ? (
          <p className="muted">{t("Loading…")}</p>
        ) : agentTokens.length === 0 ? (
          <EmptyState icon="🤖" title={t("Your agents have no tokens")} />
        ) : (
          <TokenRows tokens={agentTokens} onRevoke={(tk) => void revoke(tk as APIToken)} who={(tk) => t("for {name}", { name: session.name((tk as APIToken).principal_id) })} />
        )}
        {list && forAgent ? (
          <TokenForm
            title={t("New token for an agent")}
            scopes={list.scopes}
            lifetimes={agentLifetimes()}
            before={<AgentPicker agents={runnable} value={forAgent} onChange={setAgent} />}
            onMake={async (name, scopes, days) => {
              const r = await session.api.issueAgentToken(forAgent, name, scopes, days);
              setShown({ token: r.token, personal: false });
              await reload();
            }}
          />
        ) : (
          list && <p className="muted">{t("Make an agent in Agents to give it a token.")}</p>
        )}
      </Card>
    </Page>
  );
}

// Which of every token an admin looks at.
type Filter = "all" | "personal" | "people" | "agents" | "unused" | "expiring";

// EveryAPIToken is every API token in the workspace, for its admins: whom
// each signs in as, revoking any, and making one for any agent.
export function EveryAPIToken() {
  const session = useSession();
  const [list, loadError, reload] = useTokens(true);
  const [agents] = useAgents(true);
  const runnable = agents.filter((a) => list?.agents.includes(a.id));
  const [agent, setAgent] = useState<UUID>("");
  const forAgent = runnable.some((a) => a.id === agent) ? agent : (runnable[0]?.id ?? "");
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState<string | null>(null);
  const [error, setError] = useState("");
  const soon = Date.now() + 7 * 86400_000;
  const needle = q.trim().toLowerCase();
  const tokens = (list?.tokens ?? []).filter((tk) => {
    const isAgent = tk.principal_kind === "agent";
    const keep =
      filter === "all" ||
      (filter === "personal" && tk.personal) ||
      (filter === "people" && !isAgent) ||
      (filter === "agents" && isAgent) ||
      (filter === "unused" && !tk.last_used_at) ||
      (filter === "expiring" && new Date(tk.expires_at).getTime() < soon);
    return keep && (!needle || `${tk.name} ${session.name(tk.principal_id)}`.toLowerCase().includes(needle));
  });

  async function revoke(tk: APIToken) {
    if (!confirmRevoke(tk)) return;
    setError("");
    try {
      await session.api.revokeAPIToken(tk.id);
      setShown(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  const who = (tk: APIToken) =>
    tk.principal_kind === "agent" ? (
      t("agent {name}", { name: session.name(tk.principal_id) })
    ) : (
      <>
        {session.name(tk.principal_id)} {tk.personal ? <Badge tone="accent">{t("personal")}</Badge> : <Badge tone="warn">{t("made with lantern admin")}</Badge>}
      </>
    );

  return (
    <Card title={t("Every API token")} description={t("Tokens of people and of the agents their owners run, until they expire or are revoked. Revoking one stops whatever uses it at once.")}>
      {(loadError || error) && (
        <p className="error" role="alert">
          {loadError || error}
        </p>
      )}
      {shown && <MadeToken key={shown} token={shown} onDone={() => setShown(null)} />}
      <div className="inline-fields">
        <Field label={t("Show")}>
          <select value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">{t("All tokens")}</option>
            <option value="personal">{t("Personal tokens")}</option>
            <option value="people">{t("People's tokens")}</option>
            <option value="agents">{t("Agents' tokens")}</option>
            <option value="unused">{t("Never used")}</option>
            <option value="expiring">{t("Expiring within 7 days")}</option>
          </select>
        </Field>
        <Field label={t("Search")}>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Name, person or agent")} />
        </Field>
      </div>
      {!list ? (
        <p className="muted">{t("Loading…")}</p>
      ) : tokens.length === 0 ? (
        <EmptyState icon="🔑" title={t("No tokens")} />
      ) : (
        <TokenRows tokens={tokens} onRevoke={(tk) => void revoke(tk as APIToken)} who={(tk) => who(tk as APIToken)} />
      )}
      {list && forAgent && (
        <TokenForm
          title={t("New token for an agent")}
          scopes={list.scopes}
          lifetimes={agentLifetimes()}
          before={<AgentPicker agents={runnable} value={forAgent} onChange={setAgent} />}
          onMake={async (name, scopes, days) => {
            const r = await session.api.issueAgentToken(forAgent, name, scopes, days);
            setShown(r.token);
            await reload();
          }}
        />
      )}
    </Card>
  );
}
